import React, { useRef, useCallback } from 'react';
import { PlayIcon, PauseIcon } from '../../ios/IOSIcons';
import { Task } from '../../../../../types/task';

interface FocusPlayButtonProps {
  task: Task;
  isPlaying: boolean;
  isTimerComplete: boolean;
  isDraggingProgressBar: boolean;
  isTaskProgressDraggable: boolean;
  userProgressMap: Record<string, number>;
  remainingTimeMs: number | null;
  onPlayClick: (e: React.MouseEvent) => void;
  onPauseClick: (e: React.MouseEvent) => void;
  onRestartClick: (e: React.MouseEvent) => void;
  onMouseEnterPlayButton: () => void;
  formatRemainingTime: (ms: number | null) => string;
  t: (key: string) => string;
  playButtonRef?: React.RefObject<HTMLButtonElement>;
}

/**
 * 专注模式播放按钮组件
 * 处理播放/暂停/重启功能和倒计时显示
 */
export const FocusPlayButton: React.FC<FocusPlayButtonProps> = ({
  task,
  isPlaying,
  isTimerComplete,
  isDraggingProgressBar,
  isTaskProgressDraggable,
  userProgressMap,
  remainingTimeMs,
  onPlayClick,
  onPauseClick,
  onRestartClick,
  onMouseEnterPlayButton,
  formatRemainingTime,
  t,
  playButtonRef
}) => {
  const internalRef = useRef<HTMLButtonElement>(null);
  const buttonRef = playButtonRef || internalRef;

  return (
    <>
      {/* Play/Pause/Restart Button */}
      <button
        ref={buttonRef}
        onClick={isTimerComplete ? onRestartClick : (isPlaying ? onPauseClick : onPlayClick)}
        onMouseEnter={onMouseEnterPlayButton}
        className={`w-9 h-9 flex items-center justify-center rounded-full transition-all duration-200 ${
          isPlaying
            ? 'bg-ios-red/10 dark:bg-ios-dark-red/20 text-ios-red dark:text-ios-dark-red'
            : 'bg-ios-blue/10 dark:bg-ios-dark-blue/20 text-ios-blue dark:text-ios-dark-blue'
        } ${isDraggingProgressBar ? 'select-none' : ''}`}
        aria-label={
          isTimerComplete
            ? t('v3/focus:actions.restartTask')
            : (isPlaying ? t('v3/focus:actions.pauseTask') : t('v3/focus:actions.startTask'))
        }
      >
        <div className="w-5 h-5">
          {isPlaying && !isTimerComplete ? <PauseIcon /> : <PlayIcon />}
        </div>
      </button>

      {/* Countdown Timer - Displayed below the button when playing and session is timed */}
      {isPlaying && isTaskProgressDraggable && userProgressMap[task.id] === undefined && remainingTimeMs !== null && (
        <div className="text-xs text-ios-gray dark:text-ios-dark-gray font-mono select-none mt-0.5">
          {formatRemainingTime(remainingTimeMs)}
        </div>
      )}
    </>
  );
};
